import { addSeconds } from "date-fns";

import { ISleepStage, Measurement, SessionData, SleepStageData } from "./types";

const toSleepStage = (value: number): ISleepStage =>
  value.toString() as ISleepStage;

const isGap = (a: Date, b: Date) => Math.abs(a.getTime() - b.getTime()) > 2_000;

/** One Measurement per epoch — used as the snap grid for hover, never rendered. */
export const convertToSnapData = (
  data: SessionData | null | undefined,
): Measurement[] => {
  if (!data) return [];
  const max = Math.max(...data.sleep_stage, 1);
  return data.timestamp.map((ts, i) => ({
    datetime: ts,
    value: data.sleep_stage[i],
    normalizedValue: data.sleep_stage[i] / max,
  }));
};

// Convert 30 second interval data to intervals per stage (with variable length)
export const convertToSleepStages = (
  data: SessionData | null | undefined,
): SleepStageData[] => {
  if (!data) return [];

  const result: SleepStageData[] = [];

  data.timestamp
    .map((timestamp) => new Date(timestamp))
    .forEach((timestamp, index) => {
      const previousStage = result.at(-1);
      const currentStage = toSleepStage(data.sleep_stage[index]);

      // If we have a new stage, start a new datapoint
      if (
        !previousStage ||
        currentStage != previousStage?.stage ||
        isGap(timestamp, previousStage.endDatetime)
      ) {
        return result.push({
          stage: currentStage,
          startDatetime: timestamp,
          endDatetime: addSeconds(timestamp, 30),
        });
      }

      // If we have same datapoint and no gap, extend previous
      previousStage.endDatetime = addSeconds(timestamp, 30);
    });

  return result;
};

type EpochData = {
  timestamp: string[];
  sleep_stage: number[];
  respiration_rate_mean: number[];
  movement_fast_mean: number[];
  distance_mean: number[];
  light_ambient_mean: number[];
  sound_amplitude_mean: number[];
  signal_quality_mean: number[];
};

function convertSeries(
  timestamps: string[],
  values: number[],
  minValue: number,
) {
  const maxValue = Math.max(...values, minValue);

  return timestamps.map((date, i) => ({
    datetime: date,
    value: values[i],
    normalizedValue: maxValue === 0 ? 0 : values[i] / maxValue,
  }));
}

export const convertToSleepData = <
  T extends { id: string; dataField: keyof EpochData },
>(
  data: EpochData | null | undefined,
  dataSeries: T[],
  selection: Record<string, boolean>,
  minValues: Record<string, number> = {},
): (T & { data: Measurement[] })[] => {
  if (!data) return [];

  return dataSeries
    .filter((line) => selection[line.id])
    .map((d) => ({
      ...d,
      data: convertSeries(
        data.timestamp,
        data[d.dataField] as number[],
        minValues[d.id],
      ),
    }));
};
